import { useInfiniteQuery } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { projectsService, PROJECT_KEYS } from '@/features/projects/public';
import type { ProjectDtoPagedResult } from '@/features/projects/public';

const PAGE_SIZE = 20;

/**
 * Fetches projects for the active tenant with infinite/load-more pagination.
 * Used by SidebarProjects — lives in shared/ to avoid cross-feature imports
 * from layout components.
 *
 * @remarks
 * Only fetches non-archived projects.
 * Use `fetchNextPage()` to load the next page; `hasNextPage` signals availability.
 *
 * @example
 * const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useProjectsInfinite()
 * const projects = data?.pages.flatMap(p => p.items ?? []) ?? []
 */
export function useProjectsInfinite() {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useInfiniteQuery<ProjectDtoPagedResult>({
    queryKey: PROJECT_KEYS.myProjectsInfinite(orgId ?? ''),
    queryFn: ({ pageParam }) =>
      projectsService.getMyProjects({
        Page: pageParam as number,
        PageSize: PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.page + 1 : undefined),
    staleTime: 120_000,
    enabled: !!orgId,
  });
}
